import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { trimFilterParams } from '../../../../../core/libraries/helpers/utilities.helper';
import {
    BasePagination,
    IListResponseApiModel,
    ISingleResponseApiModel,
} from '../../../../../core/models/base-api-general.model';
import { ProductCategoryEnum } from '../../constants/product.constant';
import {
    IGetProductRentalFeeFilter,
    IProductDetail,
    IProductRentalFeeDetails,
} from '../../models/product-detail.model';
import { IProductFilterCount, IProductList, IProductListFilter, IProductSummary } from '../../models/product.model';

@Injectable({
    providedIn: 'root',
})
export class ProductService {
    private fullPathEndPoint = `${environment.apiServer}/api/v1/Products`;

    constructor(private http: HttpClient) {}

    getList(filter: IProductListFilter, pagination: BasePagination): Observable<IListResponseApiModel<IProductList>> {
        const params = this.generateListApiFilter(filter);

        params.take = pagination.pageSize;
        params.skip = pagination.currentPageIndex * pagination.pageSize;

        if (filter.search) {
            params.search = filter.search;
        }

        return this.http
            .get<ISingleResponseApiModel<IListResponseApiModel<IProductList>>>(this.fullPathEndPoint, {
                params: params,
            })
            .pipe(map((response) => response.result));
    }

    getProductFilterCount(): Observable<IProductFilterCount> {
        return this.http
            .get<ISingleResponseApiModel<IProductFilterCount>>(`${this.fullPathEndPoint}/filter-counts`)
            .pipe(map((response) => response.result));
    }

    getProductDetail(slug: string): Observable<IProductDetail> {
        return this.http
            .post<ISingleResponseApiModel<IProductDetail>>(`${this.fullPathEndPoint}/by-slug`, { slug })
            .pipe(map((response) => response.result));
    }

    getProductRentalFeeDetail(filter: IGetProductRentalFeeFilter, id: number): Observable<IProductRentalFeeDetails> {
        const params = trimFilterParams(filter);

        return this.http
            .get<ISingleResponseApiModel<IProductRentalFeeDetails>>(
                `${this.fullPathEndPoint}/${id}/rental-fee-details`,
                {
                    params: params,
                },
            )
            .pipe(map((response) => response.result));
    }

    getProductSummary(slug: string): Observable<IProductSummary> {
        return this.http
            .post<ISingleResponseApiModel<IProductSummary>>(`${this.fullPathEndPoint}/summary/by-slug`, { slug })
            .pipe(map((response) => response.result));
    }

    getProductFilterRanges(): Observable<Partial<IProductListFilter>> {
        return this.http
            .get<ISingleResponseApiModel<Partial<IProductListFilter>>>(`${this.fullPathEndPoint}/filter-ranges`)
            .pipe(map((response) => response.result));
    }

    getProductName(category: ProductCategoryEnum): Observable<IListResponseApiModel<{ id: number; name: string }>> {
        const params = {
            category: category,
        };

        return this.http
            .get<
                ISingleResponseApiModel<IListResponseApiModel<{ id: number; name: string }>>
            >(`${this.fullPathEndPoint}/names`, { params: params })
            .pipe(map((response) => response.result));
    }

    getProductModel(id: number): Observable<{ productModel: string }> {
        return this.http
            .get<ISingleResponseApiModel<{ productModel: string }>>(`${this.fullPathEndPoint}/${id}/summary`)
            .pipe(map((response) => response.result));
    }

    getUsedCategories(): Observable<ISingleResponseApiModel<ProductCategoryEnum[]>> {
        return this.http.get<ISingleResponseApiModel<ProductCategoryEnum[]>>(
            `${this.fullPathEndPoint}/used-categories`,
        );
    }

    downloadQuotation(id: number, filter: IGetProductRentalFeeFilter): Observable<HttpResponse<Blob>> {
        const params = trimFilterParams(filter);

        return this.http.get<Blob>(`${this.fullPathEndPoint}/${id}/quotation`, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            observe: 'response',
            responseType: 'blob' as 'json',
            params,
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected generateListApiFilter(filter: IProductListFilter): any {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiFilter: any = {};

        if (filter.categories?.length) {
            apiFilter.categories = filter.categories;
        }

        if (filter.industries?.length) {
            apiFilter.industries = filter.industries;
        }

        if (filter.workingEnvironments?.length) {
            apiFilter.workingEnvironments = filter.workingEnvironments;
        }

        if (filter.compareProductIds?.length) {
            apiFilter.CompareProductIds = filter.compareProductIds;
        }

        if (filter.aisleWidthCategory?.length) {
            apiFilter.aisleWidthCategory = filter.aisleWidthCategory;
        }

        const numericFilters: (keyof IProductListFilter)[] = [
            'minLoadCapacity',
            'maxLoadCapacity',
            'minLiftHeight',
            'maxLiftHeight',
        ];

        numericFilters.forEach((key) => {
            const value = filter[key];
            if (typeof value === 'number' && !isNaN(value)) {
                apiFilter[key] = value;
            }
        });

        return { ...apiFilter };
    }
}
